import React, { useState } from "react";
import { toast } from "sonner";
import { UserPlus, X, Trophy, Flame, Mail, Phone, KeyRound } from "lucide-react";
import { http } from "@/lib/api";
import { initials, avatarColor } from "@/lib/uiHelpers";

export default function AdvisorPanel({ advisors, leads, onChange, canManage = true }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const rows = advisors.map((a) => {
    const my = leads.filter((l) => l.advisor === a.name);
    return {
      ...a,
      total: my.length,
      hot: my.filter((l) => l.timeline === "Under 3 months").length,
      newCount: my.filter((l) => l.status === "New").length,
      inProgress: my.filter((l) => l.status === "In Progress" || l.status === "Contacted").length,
      closed: my.filter((l) => l.status === "Closed").length,
    };
  }).sort((a, b) => b.hot - a.hot || b.total - a.total);

  const unassigned = leads.filter((l) => !l.advisor).length;

  const add = async (e) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    setBusy(true);
    try {
      const { data } = await http.post("/advisors", {
        name: trimmed,
        email: email.trim() || null,
        phone: phone.trim() || null,
      });
      toast.success(`Added ${trimmed}`);
      setName(""); setEmail(""); setPhone(""); setShowForm(false);
      onChange(data.advisors);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add advisor");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (n) => {
    if (!window.confirm(`Remove ${n}? Their leads will be unassigned.`)) return;
    try {
      const { data } = await http.delete(`/advisors/${encodeURIComponent(n)}`);
      toast.success(`Removed ${n}`);
      onChange(data.advisors);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to remove advisor");
    }
  };

  const resetPassword = async (n) => {
    if (!window.confirm(`Generate a new password for ${n}?`)) return;
    try {
      const { data } = await http.post(`/advisors/${encodeURIComponent(n)}/reset-password`, {});
      const pw = data.new_password;
      try { await navigator.clipboard.writeText(pw); } catch (clipErr) { console.warn("Clipboard copy failed:", clipErr); }
      toast.success(`New password for ${n}: ${pw} (copied to clipboard)`, { duration: 15000 });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reset failed");
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm mb-6" data-testid="advisor-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Trophy className="w-4 h-4 text-[#C9A961]" />
          <div className="text-xs uppercase tracking-widest text-slate-500">Advisor workload</div>
        </div>
        <button
          onClick={() => setShowForm((s) => !s)}
          data-testid="advisor-toggle-form"
          className="inline-flex items-center gap-1 rounded-lg bg-[#0B1E3F] text-white px-3 py-1.5 text-xs font-semibold"
        >
          <UserPlus className="w-3.5 h-3.5" /> {showForm ? "Cancel" : "Add advisor"}
        </button>
      </div>

      {showForm && (
        <form onSubmit={add} className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-4 p-3 bg-slate-50 rounded-xl">
          <input data-testid="advisor-add-input" value={name} onChange={(e)=>setName(e.target.value)} placeholder="Full name *" className="text-sm border border-slate-200 rounded-lg px-3 py-2" />
          <input value={email} onChange={(e)=>setEmail(e.target.value)} placeholder="Email" type="email" className="text-sm border border-slate-200 rounded-lg px-3 py-2" />
          <input value={phone} onChange={(e)=>setPhone(e.target.value)} placeholder="Phone" className="text-sm border border-slate-200 rounded-lg px-3 py-2" />
          <button type="submit" data-testid="advisor-add-btn" disabled={busy || !name.trim()} className="rounded-lg bg-[#0B1E3F] text-white text-xs font-semibold disabled:opacity-50">Save</button>
        </form>
      )}

      <div className="overflow-x-auto thin-scroll">
        <table className="w-full text-sm min-w-[720px]">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-100">
              <th className="py-2 pr-4">Advisor</th>
              <th className="py-2 pr-4">Contact</th>
              <th className="py-2 pr-4 text-center">Hot</th>
              <th className="py-2 pr-4 text-center">New</th>
              <th className="py-2 pr-4 text-center">In progress</th>
              <th className="py-2 pr-4 text-center">Closed</th>
              <th className="py-2 pr-4 text-center">Total</th>
              <th className="py-2"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const c = avatarColor(r.name);
              return (
                <tr key={r.name} data-testid={`advisor-row-${r.name.replace(/\s/g,'-').toLowerCase()}`} className="border-b border-slate-50">
                  <td className="py-2 pr-4">
                    <div className="flex items-center gap-2">
                      <span
                        className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold"
                        style={{ background: c.bg, color: c.text }}
                      >
                        {initials(r.name)}
                      </span>
                      <div>
                        <div className="font-semibold text-slate-800 flex items-center gap-1">
                          {i === 0 && r.hot > 0 && <span title="Top hot-lead carrier">🏆</span>}
                          {r.name}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="py-2 pr-4 text-slate-600 text-xs">
                    {r.email && <div className="flex items-center gap-1"><Mail className="w-3 h-3 text-slate-400" />{r.email}</div>}
                    {r.phone && <div className="flex items-center gap-1 text-slate-500"><Phone className="w-3 h-3 text-slate-400" />{r.phone}</div>}
                    {!r.email && !r.phone && <span className="text-slate-300">—</span>}
                  </td>
                  <td className="py-2 pr-4 text-center">
                    {r.hot > 0 ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-red-50 text-red-700 border border-red-200 px-2 py-0.5 text-[11px] font-semibold">
                        <Flame className="w-3 h-3" /> {r.hot}
                      </span>
                    ) : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="py-2 pr-4 text-center text-slate-700">{r.newCount || <span className="text-slate-300">—</span>}</td>
                  <td className="py-2 pr-4 text-center text-slate-700">{r.inProgress || <span className="text-slate-300">—</span>}</td>
                  <td className="py-2 pr-4 text-center text-slate-700">{r.closed || <span className="text-slate-300">—</span>}</td>
                  <td className="py-2 pr-4 text-center font-semibold text-slate-800">{r.total}</td>
                  <td className="py-2 text-right">
                    <div className="inline-flex items-center gap-2">
                      <button
                        onClick={() => resetPassword(r.name)}
                        data-testid={`advisor-reset-${r.name.replace(/\s/g,'-').toLowerCase()}`}
                        title={`Reset password for ${r.name}`}
                        className="text-slate-400 hover:text-[#0B1E3F]"
                      >
                        <KeyRound className="w-4 h-4" />
                      </button>
                      <button onClick={() => remove(r.name)} data-testid={`advisor-remove-${r.name.replace(/\s/g,'-').toLowerCase()}`} className="text-slate-400 hover:text-red-600" aria-label={`Remove ${r.name}`}>
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {unassigned > 0 && (
              <tr className="border-b border-slate-50 bg-amber-50/40">
                <td className="py-2 pr-4 text-slate-600 italic">Unassigned</td>
                <td colSpan={6} className="py-2 pr-4 text-center text-slate-600">{unassigned} lead{unassigned === 1 ? "" : "s"} awaiting assignment</td>
                <td></td>
              </tr>
            )}
            {!rows.length && !unassigned && (
              <tr><td colSpan={8} className="py-6 text-center text-slate-400">Add an advisor to get started.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
